import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Plus } from "lucide-react";

const NUM_FIELDS = [
  ["quad_left", "Quad L (kg)"], ["quad_right", "Quad R (kg)"],
  ["ham_left", "Ham L (kg)"], ["ham_right", "Ham R (kg)"],
  ["calf_left", "Calf L (kg)"], ["calf_right", "Calf R (kg)"],
  ["single_hop_left", "Single hop L (cm)"], ["single_hop_right", "Single hop R (cm)"],
  ["triple_hop_left", "Triple hop L (cm)"], ["triple_hop_right", "Triple hop R (cm)"],
  ["cmj_height_cm", "CMJ height (cm)"], ["sprint_20m", "20 m sprint (s)"],
];

export default function RTSPanel({ patientId }) {
  const [tests, setTests] = useState([]);
  const [show, setShow] = useState(false);
  const [f, setF] = useState({
    injury_side: "left", psychological_readiness: 70, confidence: 70, pain_provocation: 2, clinician_criteria_met: false, notes: "",
  });

  const refresh = () => api.get(`/rts-tests/patient/${patientId}`).then((r) => setTests(r.data));
  useEffect(() => { if (patientId) refresh(); /* eslint-disable-next-line */ }, [patientId]);

  const submit = async () => {
    const payload = { patient_id: patientId, ...f };
    NUM_FIELDS.forEach(([k]) => { if (payload[k] === "" || payload[k] === undefined) payload[k] = null; });
    try {
      await api.post("/rts-tests", payload);
      toast.success("Return-to-sport test saved");
      setShow(false);
      setF({ injury_side: "left", psychological_readiness: 70, confidence: 70, pain_provocation: 2, clinician_criteria_met: false, notes: "" });
      refresh();
    } catch { toast.error("Failed to save"); }
  };

  const latest = tests[0];
  // Historical rows have been stored two ways: as ratios (0.92) and as
  // percentages (92.0). Display consistently as a whole-number percentage
  // without rewriting historical records — a value ≤ 1.5 is treated as a ratio.
  const asPct = (v) => v == null ? null : (v <= 1.5 ? v * 100 : v);
  const fmtLsi = (v) => {
    const p = asPct(v);
    return p == null ? "—" : `${Math.round(p)}%`;
  };
  const lsiCol = (v) => {
    const p = asPct(v);
    return p == null ? "text-zinc-500" : p >= 90 ? "text-green-500" : p >= 80 ? "text-amber-500" : "text-red-500";
  };

  return (
    <div className="fl-card p-6" data-testid="rts-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="fl-heading text-xl">Return-to-sport battery</div>
          <div className="text-xs text-zinc-500 mt-1">Strength LSI, hop tests, psychological readiness</div>
        </div>
        <button className="fl-btn text-xs" onClick={() => setShow(!show)} data-testid="rts-toggle"><Plus size={12} /> {show ? "Cancel" : "New test"}</button>
      </div>

      {latest && (
        <div className="mb-4 border-b border-white/10 pb-4">
          <div className="flex items-center gap-3">
            {latest.overall_ready
              ? <><CheckCircle2 className="text-green-500" size={20} /> <span className="fl-heading text-lg text-green-500">Ready to progress</span></>
              : <><XCircle className="text-amber-500" size={20} /> <span className="fl-heading text-lg text-amber-500">Not yet cleared</span></>}
            <span className="text-xs text-zinc-500 ml-auto">{new Date(latest.created_at).toLocaleDateString("en-GB")}</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
            {[["Quad LSI", latest.quad_lsi], ["Ham LSI", latest.ham_lsi], ["Calf LSI", latest.calf_lsi],
              ["Single hop LSI", latest.single_hop_lsi], ["Triple hop LSI", latest.triple_hop_lsi],
              ["Psychological", latest.psychological_readiness], ["Confidence", latest.confidence], ["Pain provocation", latest.pain_provocation]]
              .map(([l, v]) => {
                const isLsi = l.includes("LSI");
                const isPsyOrConf = l === "Psychological" || l === "Confidence";
                const display = isLsi
                  ? fmtLsi(v)
                  : (v != null ? `${v}${isPsyOrConf ? "%" : ""}` : "—");
                return (
                  <div key={l}>
                    <div className="fl-label">{l}</div>
                    <div className={`fl-heading text-2xl mt-1 ${lsiCol(v)}`}>{display}</div>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {show && (
        <div className="space-y-4">
          <div className="grid md:grid-cols-3 gap-3">
            <div>
              <div className="fl-label mb-1">Injury side</div>
              <select className="fl-input" value={f.injury_side} onChange={(e) => setF({ ...f, injury_side: e.target.value })}>
                <option value="left">Left</option><option value="right">Right</option><option value="n/a">N/A</option>
              </select>
            </div>
            {NUM_FIELDS.map(([k, l]) => (
              <div key={k}>
                <div className="fl-label mb-1">{l}</div>
                <input type="number" step="0.1" className="fl-input" value={f[k] ?? ""} onChange={(e) => setF({ ...f, [k]: e.target.value === "" ? "" : parseFloat(e.target.value) })} data-testid={`rts-${k}`} />
              </div>
            ))}
          </div>
          <div className="grid md:grid-cols-3 gap-3">
            <div>
              <div className="fl-label mb-1">Psychological readiness (0-100)</div>
              <input type="number" min="0" max="100" className="fl-input" value={f.psychological_readiness} onChange={(e) => setF({ ...f, psychological_readiness: parseInt(e.target.value) || 0 })} data-testid="rts-psych" />
            </div>
            <div>
              <div className="fl-label mb-1">Confidence (0-100)</div>
              <input type="number" min="0" max="100" className="fl-input" value={f.confidence} onChange={(e) => setF({ ...f, confidence: parseInt(e.target.value) || 0 })} />
            </div>
            <div>
              <div className="fl-label mb-1">Pain provocation (0-10)</div>
              <input type="number" min="0" max="10" className="fl-input" value={f.pain_provocation} onChange={(e) => setF({ ...f, pain_provocation: parseInt(e.target.value) || 0 })} />
            </div>
          </div>
          <textarea className="fl-input" placeholder="Notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
          <label className="flex items-center gap-2 text-sm text-zinc-300">
            <input type="checkbox" checked={f.clinician_criteria_met} onChange={(e) => setF({ ...f, clinician_criteria_met: e.target.checked })} data-testid="rts-criteria" />
            All clinician criteria met (movement quality, control, sport-specific drills)
          </label>
          <button className="fl-btn" onClick={submit} data-testid="rts-save">Save test</button>
        </div>
      )}

      {tests.length > 1 && (
        <div className="mt-4">
          <div className="fl-label mb-2">History</div>
          <div className="space-y-2">
            {tests.slice(1).map((t) => (
              <div key={t.id} className="flex justify-between text-xs text-zinc-400 py-1 border-b border-white/5">
                <span>{new Date(t.created_at).toLocaleDateString("en-GB")}</span>
                <span>Quad LSI: <span className={lsiCol(t.quad_lsi)}>{fmtLsi(t.quad_lsi)}</span></span>
                <span>{t.overall_ready ? "Ready" : "Not yet"}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
